/**
 * Voyage Map Composable
 * Handles Leaflet map initialization, voyage track drawing, and ship markers
 */
/* eslint-disable no-undef */
/* eslint-disable no-use-before-define */

import moment from 'moment';
import {
  createShipIcons,
  createShipTooltip,
  createDotTrailPopup,
  extractCoordinatesFromGpsData,
  getLastLatLngFromGpsData,
  getLastTrackFromGpsData,
  isShipOnline,
} from '@/views/voyage/utils/mapIcons';

export function useVoyageMap() {
  let mapInstance = null;
  let markerLayer = null;
  let polyline = null;
  let shipLayerGroup = null;
  let icons = null;
  let landmarkLayerGroup = null;
  let baseLayers = {};
  let activeBaseLayer = null;
  let activeBaseLayerKey = null;
  let showLandmarks = true;
  let showGpsTrack = true;
  let showDotTrail = true;
  let dotTrailMarkers = [];

  /**
   * Load Leaflet CSS
   */
  const loadLeafletCSS = () => {
    if (!document.querySelector('link[href="/style/leaflet.css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = '/style/leaflet.css';
      document.head.appendChild(link);
    }
  };

  /**
   * Ensure Leaflet JS is loaded
   * @returns {Promise<boolean>} True if Leaflet is loaded
   */
  const ensureLeafletLoaded = async () => {
    if (typeof L !== 'undefined') return true;

    const existingScript = document.querySelector('script[src="/script/leaflet.js"]');
    if (existingScript) {
      await new Promise((resolve, reject) => {
        if (typeof L !== 'undefined') {
          resolve();
          return;
        }
        existingScript.addEventListener('load', resolve, { once: true });
        existingScript.addEventListener('error', reject, { once: true });
      });
      return typeof L !== 'undefined';
    }

    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = '/script/leaflet.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return typeof L !== 'undefined';
  };

  /**
   * Initialize the voyage map
   * @param {string} containerId - DOM element ID for map container
   * @returns {Object|null} Map instance or null
   */
  const initMap = (containerId = 'voyage-map') => {
    const mapContainer = document.getElementById(containerId);

    if (!mapContainer || typeof L === 'undefined') {
      return null;
    }

    mapInstance = L.map(containerId, {
      zoomControl: true,
    }).setView([-5.95, 106.95], 10);

    // Define base layers
    const googleSatellite = L.tileLayer('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}&gl=id&hl=id', {
      maxZoom: 19,
      attribution: 'Map data © Google Maps',
    });

    const googleHybrid = L.tileLayer('https://mt1.google.com/vt/lyrs=h&x={x}&y={y}&z={z}&gl=id&hl=id', {
      maxZoom: 19,
      attribution: 'Map data © Google Maps',
    });

    const osmLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    });

    baseLayers = {
      satellite: googleSatellite,
      hybrid: googleHybrid,
      osm: osmLayer,
    };

    // Set default layer (satellite)
    const storedLayer = localStorage.getItem('voyageMapBaseLayer') || 'satellite';
    setBaseLayer(storedLayer);

    markerLayer = L.layerGroup().addTo(mapInstance);
    landmarkLayerGroup = L.layerGroup().addTo(mapInstance);

    // Add map settings control
    addMapSettingsControl();

    return mapInstance;
  };

  /**
   * Draw GPS track on the map
   * @param {Object} gpsData - GPS data object
   * @returns {Object} Object with coordinates and coordinatesData
   */
  const drawGpsTrack = (gpsData) => {
    if (!gpsData || !gpsData.data || !mapInstance) {
      return { coordinates: [], coordinatesData: [] };
    }

    const { coordinates, coordinatesData } = extractCoordinatesFromGpsData(gpsData);

    if (coordinates.length < 2) {
      return { coordinates, coordinatesData };
    }

    // Remove existing polyline if any
    if (polyline) {
      polyline.remove();
    }

    // Draw polyline (voyage track)
    const online = isShipOnline(gpsData.lastTimestamp);
    polyline = L.polyline(coordinates, {
      color: online ? '#28F385' : '#FF3B30',
      weight: 4,
      opacity: 0.8,
      smoothFactor: 1,
    }).addTo(mapInstance);

    // Fit map to polyline bounds
    mapInstance.fitBounds(polyline.getBounds(), {
      padding: [50, 50],
    });

    return { coordinates, coordinatesData };
  };

  /**
   * Add ship to the map
   * @param {Object} asset - Asset object
   * @param {Object} gpsData - GPS data object
   * @param {Object} voyage - Voyage object
   * @returns {boolean} True if ship was added successfully
   */
  const addShipToMap = (asset, gpsData, voyage) => {
    if (!mapInstance || !asset) {
      return false;
    }

    // Get last known position from voyage
    const lastLatLng = getLastLatLngFromGpsData(gpsData);
    if (!lastLatLng) {
      return false;
    }

    // Get last GPS timestamp for icon state
    const lastGpsTimestamp = voyage?.mvygEndTime ? moment(voyage.mvygEndTime).unix() : 0;

    // Create ship icons
    icons = createShipIcons(asset, lastGpsTimestamp);

    // Create layer group for the ship
    shipLayerGroup = L.layerGroup().addTo(mapInstance);

    // Create main marker
    const marker = L.marker(lastLatLng, { icon: icons.shipIcon });
    marker.bindTooltip(
      createShipTooltip(asset.massName, !icons.isOffline),
      { permanent: true, offset: [20, 10] },
    ).openTooltip();

    // Create heading marker (rotated based on track)
    const headingMarker = L.marker(lastLatLng, {
      icon: icons.shipHeadingIcon,
      rotationAngle: getLastTrackFromGpsData(gpsData) || 0,
      rotationOrigin: '50% 50%',
    });

    // Add markers to layer group
    shipLayerGroup.addLayer(marker);
    shipLayerGroup.addLayer(headingMarker);

    return true;
  };

  /**
   * Add dot trail markers along the voyage track
   * @param {Array} coordinatesData - Array of coordinate data points
   * @returns {number} Number of markers added
   */
  const addDotTrailMarkers = (coordinatesData) => {
    if (!icons?.shipMarkerIcon || !markerLayer || !coordinatesData || coordinatesData.length === 0) {
      return 0;
    }

    // Clear existing dot trail markers (but keep start/end markers)
    markerLayer.eachLayer((layer) => {
      if (layer instanceof L.Marker) {
        markerLayer.removeLayer(layer);
      }
    });

    dotTrailMarkers = [];

    // Only add markers if showDotTrail is enabled
    if (!showDotTrail) {
      return coordinatesData.length;
    }

    coordinatesData.forEach((data) => {
      const marker = L.marker(data.latLong, {
        icon: icons.shipMarkerIcon,
        rotationAngle: data.heading || 0,
        rotationOrigin: '50% 50%',
      });

      marker.bindPopup(createDotTrailPopup(data));
      markerLayer.addLayer(marker);
      dotTrailMarkers.push(marker);
    });

    return coordinatesData.length;
  };

  /**
   * Destroy the map and clean up resources
   */
  const destroyMap = () => {
    if (mapInstance) {
      mapInstance.off();
      mapInstance.remove();
      mapInstance = null;
    }
    markerLayer = null;
    if (polyline) {
      polyline.remove();
      polyline = null;
    }
    if (shipLayerGroup) {
      shipLayerGroup.clearLayers();
      shipLayerGroup = null;
    }
    if (landmarkLayerGroup) {
      landmarkLayerGroup.clearLayers();
      landmarkLayerGroup = null;
    }
    if (activeBaseLayer) {
      activeBaseLayer = null;
    }
    baseLayers = {};
    activeBaseLayerKey = null;
    icons = null;
  };

  /**
   * Set active base layer
   * @param {string} layerKey - Layer key (satellite, hybrid, osm)
   */
  const setBaseLayer = (layerKey) => {
    if (!mapInstance || !baseLayers[layerKey]) return;

    // Remove current active layer
    if (activeBaseLayer) {
      mapInstance.removeLayer(activeBaseLayer);
    }

    // Set new active layer
    activeBaseLayer = baseLayers[layerKey];
    activeBaseLayerKey = layerKey;
    activeBaseLayer.addTo(mapInstance);

    // Save to localStorage
    localStorage.setItem('voyageMapBaseLayer', layerKey);

    // Update UI
    updateSettingsUI();
  };

  /**
   * Add map settings control
   */
  const addMapSettingsControl = () => {
    if (!mapInstance) return;

    const SettingsControl = L.Control.extend({
      onAdd: () => {
        const container = L.DomUtil.create('div', 'voyage-map-settings');
        container.innerHTML = `
          <button class="settings-toggle" type="button" title="Map Settings">
            <i class="ri-settings-3-line"></i>
          </button>
          <div class="settings-panel hidden">
            <div class="settings-header">Base Map</div>
            <label class="settings-option">
              <input type="radio" name="voyage-base-layer" value="satellite" />
              <span>Satellite</span>
            </label>
            <label class="settings-option">
              <input type="radio" name="voyage-base-layer" value="hybrid" />
              <span>Hybrid</span>
            </label>
            <label class="settings-option">
              <input type="radio" name="voyage-base-layer" value="osm" />
              <span>OSM</span>
            </label>
            <div class="settings-separator"></div>
            <div class="settings-header">Layers</div>
            <label class="settings-option">
              <input type="checkbox" name="voyage-show-landmarks" checked />
              <span>Landmarks</span>
            </label>
            <label class="settings-option">
              <input type="checkbox" name="voyage-show-gps-track" checked />
              <span>GPS Track</span>
            </label>
            <label class="settings-option">
              <input type="checkbox" name="voyage-show-dot-trail" checked />
              <span>Dot Trail</span>
            </label>
          </div>
        `;

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        // Toggle panel
        const toggleBtn = container.querySelector('.settings-toggle');
        const panel = container.querySelector('.settings-panel');
        toggleBtn.addEventListener('click', () => {
          panel.classList.toggle('hidden');
        });

        // Layer change handler
        const radioInputs = container.querySelectorAll('input[name="voyage-base-layer"]');
        radioInputs.forEach((input) => {
          input.addEventListener('change', (e) => {
            if (e.target.checked) {
              setBaseLayer(e.target.value);
            }
          });
        });

        // Toggle handlers
        const landmarksToggle = container.querySelector('input[name="voyage-show-landmarks"]');
        const gpsTrackToggle = container.querySelector('input[name="voyage-show-gps-track"]');
        const dotTrailToggle = container.querySelector('input[name="voyage-show-dot-trail"]');

        if (landmarksToggle) {
          landmarksToggle.addEventListener('change', (e) => {
            toggleLandmarks(e.target.checked);
          });
        }

        if (gpsTrackToggle) {
          gpsTrackToggle.addEventListener('change', (e) => {
            toggleGpsTrack(e.target.checked);
          });
        }

        if (dotTrailToggle) {
          dotTrailToggle.addEventListener('change', (e) => {
            toggleDotTrail(e.target.checked);
          });
        }

        return container;
      },
    });

    mapInstance.addControl(new SettingsControl({ position: 'topleft' }));
  };

  /**
   * Update settings UI to match current state
   */
  const updateSettingsUI = () => {
    const panel = document.querySelector('.voyage-map-settings .settings-panel');
    if (!panel) return;

    const radioInput = panel.querySelector(`input[name="voyage-base-layer"][value="${activeBaseLayerKey}"]`);
    if (radioInput) {
      radioInput.checked = true;
    }

    const landmarksInput = panel.querySelector('input[name="voyage-show-landmarks"]');
    const gpsTrackInput = panel.querySelector('input[name="voyage-show-gps-track"]');
    const dotTrailInput = panel.querySelector('input[name="voyage-show-dot-trail"]');

    if (landmarksInput) landmarksInput.checked = showLandmarks;
    if (gpsTrackInput) gpsTrackInput.checked = showGpsTrack;
    if (dotTrailInput) dotTrailInput.checked = showDotTrail;
  };

  /**
   * Toggle landmarks visibility
   */
  const toggleLandmarks = (visible) => {
    showLandmarks = visible;
    if (!landmarkLayerGroup) return;

    if (visible) {
      landmarkLayerGroup.addTo(mapInstance);
    } else {
      landmarkLayerGroup.remove();
    }
  };

  /**
   * Toggle GPS track visibility
   */
  const toggleGpsTrack = (visible) => {
    showGpsTrack = visible;
    if (!polyline) return;

    if (visible) {
      polyline.addTo(mapInstance);
    } else {
      polyline.remove();
    }
  };

  /**
   * Toggle dot trail visibility
   */
  const toggleDotTrail = (visible) => {
    showDotTrail = visible;
    if (!markerLayer || dotTrailMarkers.length === 0) return;

    // Clear all dot trail markers
    dotTrailMarkers.forEach((marker) => {
      marker.remove();
    });

    // Re-add if visible
    if (visible) {
      dotTrailMarkers.forEach((marker) => {
        marker.addTo(markerLayer);
      });
    }
  };

  /**
   * Get the map instance
   * @returns {Object|null} Map instance or null
   */
  const getMapInstance = () => mapInstance;

  /**
   * Add landmark markers (Origin & Destination) to the map
   * @param {Object} origin - Origin landmark data
   * @param {Object} destination - Destination landmark data
   * @returns {number} Number of markers added
   */
  const addLandmarkMarkers = (origin, destination) => {
    if (!mapInstance || !landmarkLayerGroup) return 0;

    // Clear existing landmark markers
    landmarkLayerGroup.clearLayers();

    let markersAdded = 0;

    // Create custom icons for Origin (A) and Destination (B)
    const originIcon = L.divIcon({
      className: 'landmark-marker origin-marker',
      html: '<div class="marker-badge">A</div>',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32],
    });

    const destinationIcon = L.divIcon({
      className: 'landmark-marker destination-marker',
      html: '<div class="marker-badge">B</div>',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32],
    });

    // Add Origin marker
    if (origin?.lndmLatitude && origin?.lndmLongitude) {
      const lat = parseFloat(origin.lndmLatitude);
      const lng = parseFloat(origin.lndmLongitude);

      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        const originMarker = L.marker([lat, lng], { icon: originIcon })
          .bindPopup(`<b>Origin</b><br>${origin.lndmName || 'N/A'}`);
        landmarkLayerGroup.addLayer(originMarker);
        markersAdded++;
      }
    }

    // Add Destination marker
    if (destination?.lndmLatitude && destination?.lndmLongitude) {
      const lat = parseFloat(destination.lndmLatitude);
      const lng = parseFloat(destination.lndmLongitude);

      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        const destMarker = L.marker([lat, lng], { icon: destinationIcon })
          .bindPopup(`<b>Destination</b><br>${destination.lndmName || 'N/A'}`);
        landmarkLayerGroup.addLayer(destMarker);
        markersAdded++;
      }
    }

    return markersAdded;
  };

  /**
   * Get the marker layer
   * @returns {Object|null} Marker layer or null
   */
  const getMarkerLayer = () => markerLayer;

  return {
    // Initialization
    loadLeafletCSS,
    ensureLeafletLoaded,
    initMap,
    // Drawing functions
    drawGpsTrack,
    addShipToMap,
    addDotTrailMarkers,
    addLandmarkMarkers,
    // Cleanup
    destroyMap,
    // Getters
    getMapInstance,
    getMarkerLayer,
    // Settings
    setBaseLayer,
    toggleLandmarks,
    toggleGpsTrack,
    toggleDotTrail,
  };
}

export default useVoyageMap;
